import { autorun } from 'mobx';
import moment from 'moment-timezone';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';

import { DEFAULT_PAGE_SIZE, normalizePageSize } from 'pages/CrewTimeline/const';
import { crewTimelineStore } from 'pages/CrewTimeline/CrewTimelineStore';
import {
	clearFilters,
	deserializeCitiesToFormValues,
	loadFilters,
} from 'pages/CrewTimeline/filterPersistence';

import { ALL_GROUPS_ID, EMPTY_FORM_VALUES } from './const';
import { EXTRA_BOARD_CLASSIFICATION_OPTION } from './OperatorTypeFilter/const';

/**
 * Transforms raw react-hook-form values into the filter shape the store expects.
 */
const calendarDateToTimestamp = (date) => {
	const tz = crewTimelineStore.selectedTimezone?.value || moment.tz.guess();

	return moment
		.tz({ year: date.getFullYear(), month: date.getMonth(), day: date.getDate() }, tz)
		.toISOString();
};

const transformFormDataToFilters = (data) => {
	const timestamp = data.dateRange?.from ? calendarDateToTimestamp(data.dateRange.from) : undefined;

	const filters = {
		display: parseInt(data.display, 10) || 1,
		pageSize: normalizePageSize(data.pageSize),
		...(data.text && { text: data.text }),
		...(data.group && { groupsIds: [data.group] }),
		...(data.crewMember && { crewMemberId: data.crewMember.key }),
		...(data.operatorType && { operatorType: data.operatorType }),
		...(data.status && { status: data.status }),
		...(timestamp && { fromTimestamp: timestamp, toTimestamp: timestamp }),
	};

	const hasCities = data.locationAvailabilityCity?.length;

	if (!hasCities) return filters;

	const cityIds = data.locationAvailabilityCity
		.map(({ value, key, id }) => value || key || id)
		.filter(Boolean);

	if (cityIds.length) {
		filters.locationAvailabilityCityIds = cityIds;
		filters.locationAvailabilityCity = data.locationAvailabilityCity;
	}

	return filters;
};

export function useTimelineFilters({ onFiltersSubmitted }) {
	const hasRestoredRef = useRef(false);
	const [dateKey, setDateKey] = useState(0);
	const {
		control,
		formState: { dirtyFields },
		handleSubmit,
		resetField,
		reset,
		getValues,
		setValue,
	} = useForm({ mode: 'onChange', defaultValues: EMPTY_FORM_VALUES });

	const submitValues = useCallback(
		(data) => {
			const filters = transformFormDataToFilters(data);

			onFiltersSubmitted(filters);
		},
		[onFiltersSubmitted]
	);

	const submitCurrentValues = useCallback(() => {
		submitValues(getValues());
	}, [submitValues, getValues]);

	const handleReset = useCallback(() => {
		clearFilters();
		reset(EMPTY_FORM_VALUES);
		submitValues(EMPTY_FORM_VALUES);
	}, [reset, submitValues]);

	const handleResetSingleField = useCallback(
		(fieldname) => {
			resetField(fieldname);

			if (fieldname === 'dateRange') {
				crewTimelineStore.navigateToToday();
				return;
			}

			submitCurrentValues();
		},
		[resetField, submitCurrentValues]
	);

	const restoreFilters = useCallback(() => {
		const stored = loadFilters();
		const dateFromStore = {
			from: new Date(crewTimelineStore.currentStartDate),
		};

		if (!stored) return setValue('dateRange', dateFromStore);

		const { group, status, locationAvailabilityCity, operatorType, displayDays, pageSize } = stored;
		const savedGroup = group && group !== ALL_GROUPS_ID ? group : '';
		const savedStatus = status || '';
		const savedOperatorType = operatorType || EXTRA_BOARD_CLASSIFICATION_OPTION;
		const savedDisplay = Number(displayDays) || 1;
		const savedPageSize = normalizePageSize(pageSize);
		const savedCities = deserializeCitiesToFormValues(locationAvailabilityCity);

		const hasNonDefaultValues =
			savedGroup ||
			savedStatus ||
			!!savedCities.length ||
			savedOperatorType !== EXTRA_BOARD_CLASSIFICATION_OPTION ||
			savedDisplay !== 1 ||
			savedPageSize !== DEFAULT_PAGE_SIZE;

		if (!hasNonDefaultValues) return setValue('dateRange', dateFromStore);

		reset({
			...EMPTY_FORM_VALUES,
			group: savedGroup,
			status: savedStatus,
			operatorType: savedOperatorType,
			display: savedDisplay,
			pageSize: savedPageSize,
			locationAvailabilityCity: savedCities,
			dateRange: dateFromStore,
		});

		submitCurrentValues();
	}, [setValue, reset, submitCurrentValues]);

	useEffect(() => {
		const dispose = autorun(() => {
			const dateRange = crewTimelineStore.formattedDateRange;

			setValue('dateRange', dateRange, { shouldDirty: false, shouldValidate: false });
			setDateKey((prev) => prev + 1);
		});

		return () => dispose();
	}, [setValue]);

	useEffect(() => {
		if (hasRestoredRef.current) return;

		hasRestoredRef.current = true;

		restoreFilters();
	}, [restoreFilters]);

	return {
		control,
		dateKey,
		dirtyFields,
		selectedTimezone: crewTimelineStore.selectedTimezone,
		handleTimezoneChange: crewTimelineStore.setSelectedTimezone,
		formSubmitHandler: handleSubmit(submitValues),
		submitOnChange: submitCurrentValues,
		setValue,
		handleReset,
		handleResetSingleField,
	};
}
